import { useReducer, useCallback, useRef, useEffect } from 'react'
import {
  undoRedoReducer,
  initialUndoRedoState,
  PUSH_HISTORY,
  UNDO,
  REDO,
  CLEAR_HISTORY,
  CLEAR_REDO_HISTORY,
  RESTORE_HISTORY,
} from './undoRedoReducer'
import {
  TURN_PAGE,
  UPDATE_VIEWING_PAGES,
  SET_MOBILE_MODE,
  SET_BOOK,
  RESTORE_FROM_SNAPSHOT,
} from '../../providers/MemoryBooksReducer'
import { saveHistoryToStorage, loadHistoryFromStorage, clearHistoryFromStorage } from './sessionStorageSync'
import type { MemoryBookSnapshot, UseUndoRedoReturn } from './types'

// Actions excluded from undo history (won't trigger history snapshots)
const UNDO_EXCLUDED_ACTIONS = new Set([
  TURN_PAGE,
  UPDATE_VIEWING_PAGES,
  SET_MOBILE_MODE,
  SET_BOOK, // Initial load shouldn't be undoable
  RESTORE_FROM_SNAPSHOT, // Undo/redo restore shouldn't be undoable
])

export function useUndoRedo(
  bookId: string | null,
  onUndo?: ((snapshot: MemoryBookSnapshot) => void) | null,
  onRedo?: ((snapshot: MemoryBookSnapshot) => void) | null
): UseUndoRedoReturn {
  const [state, dispatch] = useReducer(undoRedoReducer, initialUndoRedoState)
  const currentSnapshotRef = useRef<MemoryBookSnapshot | null>(null)
  const previousBookIdRef = useRef<string | null>(bookId)
  const isInitializedRef = useRef<boolean>(false)

  // Clear history when switching books or when disabled
  useEffect(() => {
    // On initial mount, if bookId is undefined, don't clear storage yet
    // This prevents clearing saved history during page refresh before bookId loads
    if (!bookId && !isInitializedRef.current) {
      // Just mark as initialized but don't clear - let the restoration effect run when bookId arrives
      isInitializedRef.current = true
      return
    }

    if (!bookId) {
      // Feature is disabled after being initialized, clear everything
      dispatch({ type: CLEAR_HISTORY })
      clearHistoryFromStorage()
      currentSnapshotRef.current = null
      isInitializedRef.current = true
      return
    }

    // Clear history when switching between different books (both must be defined)
    if (previousBookIdRef.current && bookId && previousBookIdRef.current !== bookId) {
      dispatch({ type: CLEAR_HISTORY })
      clearHistoryFromStorage()
      currentSnapshotRef.current = null
    }

    previousBookIdRef.current = bookId
    isInitializedRef.current = true
  }, [bookId])

  // Load history from sessionStorage on mount (only if enabled)
  useEffect(() => {
    if (!bookId) return // Feature disabled

    const restoredHistory = loadHistoryFromStorage(bookId)
    if (restoredHistory) {
      // Restore both undo and redo stacks using dedicated action
      dispatch({
        type: RESTORE_HISTORY,
        payload: {
          undoStack: restoredHistory.undoStack,
          redoStack: restoredHistory.redoStack,
        },
      })

      // If there was a current snapshot, restore it to local state only (no onUndo callback)
      // This is just session hydration, not an actual undo action
      if (restoredHistory.currentSnapshot) {
        currentSnapshotRef.current = restoredHistory.currentSnapshot
      }
    }
  }, [bookId])

  // Save history to sessionStorage on unmount and visibility change (only if enabled)
  useEffect(() => {
    if (!bookId) return undefined // Feature disabled

    const handleBeforeUnload = (): void => {
      saveHistoryToStorage(state, bookId, currentSnapshotRef.current)
    }

    const handleVisibilityChange = (): void => {
      if (document.hidden) {
        saveHistoryToStorage(state, bookId, currentSnapshotRef.current)
      }
    }

    window.addEventListener('beforeunload', handleBeforeUnload)
    document.addEventListener('visibilitychange', handleVisibilityChange)

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload)
      document.removeEventListener('visibilitychange', handleVisibilityChange)

      // Save history on unmount (no state dispatch during unmount)
      saveHistoryToStorage(state, bookId, currentSnapshotRef.current)
    }
  }, [state, bookId])

  const pushHistory = useCallback(
    (snapshot: MemoryBookSnapshot, actionType: string) => {
      if (UNDO_EXCLUDED_ACTIONS.has(actionType)) {
        return
      }

      dispatch({
        type: PUSH_HISTORY,
        payload: {
          snapshot: {
            ...snapshot,
            actionType,
            timestamp: Date.now(),
          },
        },
      })

      currentSnapshotRef.current = null
    },
    [dispatch]
  )

  const undo = useCallback(
    (currentState: MemoryBookSnapshot): MemoryBookSnapshot | null => {
      if (state.undoStack.length === 0) {
        return null
      }

      const previousState = state.undoStack.at(-1) ?? null

      if (!previousState) {
        return null
      }

      dispatch({
        type: UNDO,
        payload: {
          currentState: {
            ...currentState,
            timestamp: Date.now(),
          },
        },
      })

      currentSnapshotRef.current = previousState

      if (onUndo) {
        onUndo(previousState)
      }

      return previousState
    },
    [state.undoStack, onUndo, dispatch]
  )

  const redo = useCallback(
    (currentState: MemoryBookSnapshot): MemoryBookSnapshot | null => {
      if (state.redoStack.length === 0) {
        return null
      }

      const nextState = state.redoStack.at(-1) ?? null

      if (!nextState) {
        return null
      }

      dispatch({
        type: REDO,
        payload: {
          currentState: {
            ...currentState,
            timestamp: Date.now(),
          },
        },
      })

      currentSnapshotRef.current = nextState

      if (onRedo) {
        onRedo(nextState)
      }

      return nextState
    },
    [state.redoStack, onRedo, dispatch]
  )

  const clearHistory = useCallback(() => {
    currentSnapshotRef.current = null

    dispatch({ type: CLEAR_HISTORY })
    clearHistoryFromStorage()
  }, [dispatch])

  const clearRedoHistory = useCallback(() => {
    dispatch({ type: CLEAR_REDO_HISTORY })
  }, [dispatch])

  return {
    canUndo: state.undoStack.length > 0,
    canRedo: state.redoStack.length > 0,
    undoStackSize: state.undoStack.length,
    redoStackSize: state.redoStack.length,
    pushHistory,
    undo,
    redo,
    clearHistory,
    clearRedoHistory,
  }
}
